/**
 * API client — wraps all fetch calls to /api/*.
 * Credentials (session cookie) are always included.
 */

const BASE = '/api';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error((body as { error?: string }).error ?? res.statusText);
    (err as Error & { status: number }).status = res.status;
    throw err;
  }

  return res.json() as Promise<T>;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const authApi = {
  login: (password: string) =>
    request<{ ok: boolean }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  logout: () =>
    request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  me: () =>
    request<{ authenticated: boolean; exp?: number }>('/auth/me'),
};

// ─── Dashboard ────────────────────────────────────────────────────────────────

export type DashboardData = {
  assets: {
    pensions: Record<string, number>;
    isas: Array<{ id: string; name: string; value_gbp: number }>;
    gia: Array<{ id: string; name: string; value_gbp: number }>;
    crypto: Array<{ id: string; name: string; value_gbp: number }>;
    nora_jisa_gbp: number | null;
    cash_savings_gbp: number;
    property_gbp: number;
  };
  liabilities: {
    mortgages: Array<{ id: number; label: string; lender: string; balance: number }>;
    credit_card_gbp: number;
  };
  totals: {
    total_assets: number;
    total_liabilities: number;
    net_worth_inc_property: number;
    net_worth_exc_property: number;
  };
  snapshots: Array<{
    snapshot_date: string;
    label: string;
    category: string;
    value_gbp: number;
  }>;
};

export const dashboardApi = {
  get: () => request<DashboardData>('/dashboard'),
  lockMonth: () => request<{ ok: boolean; snapshot_date: string }>('/dashboard/lock-month', { method: 'POST' }),
  updateSettings: (data: { cash_savings_gbp?: number; credit_card_gbp?: number }) =>
    request<{ ok: boolean }>('/dashboard/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

// ─── Portfolio ────────────────────────────────────────────────────────────────

export type Portfolio = {
  id: string;
  name: string;
  type: 'isa' | 'gia' | 'crypto';
  provider: string;
  currency: string;
  owner: string;
};

export type HoldingWithPrice = {
  ticker: string | null;
  isin: string | null;
  asset_name: string;
  units: number;
  cost_basis_gbp: number;
  dividends_received: number;
  price_gbp: number | null;
  price_fetched_at: string | null;
};

export type HoldingsResponse = {
  portfolio: Portfolio;
  holdings: HoldingWithPrice[];
};

export type TransactionsResponse = {
  portfolio: Portfolio;
  transactions: {
    id: number;
    portfolio_id: string;
    trade_date: string;
    settle_date: string | null;
    type: string;
    ticker: string | null;
    isin: string | null;
    asset_name: string;
    quantity: number | null;
    price_native: number | null;
    native_currency: string;
    fx_rate: number;
    total_gbp: number;
    fees_gbp: number;
    notes: string | null;
  }[];
  limit: number;
  offset: number;
};

export type PensionPot = {
  id: number;
  owner: 'Jon' | 'Sarah';
  type: 'employer-dc' | 'personal-dc' | 'db' | 'state';
  name: string;
  provider: string;
  currentValue: number;
  valueUpdatedAt?: string;
  employeeMonthly: number;
  employerMonthly: number;
  accruedAnnualIncome?: number;
  projectedAnnualIncome?: number;
};

export type PensionsResponse = {
  settings: Record<string, unknown>;
  pots: PensionPot[];
};

export type NoraFund = {
  isin: string;
  name: string;
  units: number;
};

export type NoraResponse = {
  nora: { funds: NoraFund[]; transactions: unknown[]; openedDate: string } | null;
  fund_price_gbp: number | null;
  total_value_gbp: number | null;
};

export type PriceRefreshResult = {
  ok: boolean;
  refreshed: number;
  failed: number;
  results: { ticker: string; success: boolean; price_gbp?: number; currency?: string }[];
  message?: string;
};

export const portfolioApi = {
  list: () => request<Portfolio[]>('/portfolio'),

  holdings: (id: string) => request<HoldingsResponse>(`/portfolio/${id}/holdings`),

  transactions: (id: string, limit = 200, offset = 0) =>
    request<TransactionsResponse>(
      `/portfolio/${id}/transactions?limit=${limit}&offset=${offset}`,
    ),

  pensions: () => request<PensionsResponse>('/portfolio/pensions'),

  nora: () => request<NoraResponse>('/portfolio/nora'),

  refreshPrices: (id: string) =>
    request<PriceRefreshResult>(`/portfolio/${id}/prices/refresh`, { method: 'POST' }),
};

// ─── Mortgage ─────────────────────────────────────────────────────────────────

export type MortgageData = {
  mortgages: Array<{
    id: number;
    label: string;
    lender: string;
    balance: number;
    originalBalance: number;
    rate: number;
    monthlyPayment: number;
    termMonths: number;
    startDate: string;
    rateType: string;
    rateEndDate: string;
    propertyValue: number;
    history: Array<{ date: string; balance: number }>;
    overpayments: Array<{ date: string; amount: number }>;
    lumpSums: Array<{ date: string; amount: number }>;
  }>;
  summary: {
    property_value_gbp: number;
    total_balance_gbp: number;
    equity_gbp: number;
    ltv_percent: number;
  };
};

export const mortgageApi = {
  get: () => request<MortgageData>('/mortgage'),
};

// ─── Import ───────────────────────────────────────────────────────────────────

export type ImportResult = {
  imported: number;
  skipped_duplicates: number;
  errors: string[];
  date_range: { from: string; to: string } | null;
};

export const importApi = {
  upload: async (file: File, portfolioId: string): Promise<ImportResult> => {
    const form = new FormData();
    form.append('file', file);
    form.append('portfolio_id', portfolioId);
    const res = await fetch('/api/import', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error((body as { error?: string }).error ?? res.statusText);
    }
    return res.json() as Promise<ImportResult>;
  },
};
